import ImageEditor from "@react-native-community/image-editor";
import React, { useState, useEffect, useCallback, useMemo, useRef, WheelEvent } from "react";
import { Animated, View, StyleSheet, Platform } from "react-native";
import {
    State,
    PinchGestureHandler,
    PanGestureHandler,
    GestureEvent,
} from "react-native-gesture-handler";
import MaskedView from "@react-native-masked-view/masked-view";

import {
    Size,
    round,
    assert,
    getValue,
    getAlpha,
    isInRange,
    computeScale,
    computeCover,
    computeContain,
    translateRangeX,
    computeImageSize,
    computeTranslation,
    translateRangeY,
    computeScaledWidth,
    computeScaledHeight,
    computeScaledMultiplier,
    computeTranslate,
    computeOffset,
    computeSize,
} from "./utils";

/***
 * original : https://github.com/vemarav/react-native-avatar-crop/blob/main/package/src/crop/index.tsx
 ***/

const DEFAULT_ANIM_DURATION = 180;

export type CropProps = {
    source: { uri: string; size?: Size };
    cropShape?: "rect" | "circle";
    cropArea: Size;
    borderWidth?: number;
    backgroundColor?: string;
    opacity?: number;
    width: number;
    height: number;
    maxZoom?: number;
    resizeMode?: "contain" | "cover";
    onCrop: (
        cropCallback: (quality?: number) => Promise<{
            uri: string;
            width: number;
            height: number;
        }>,
    ) => void;
};

const Crop = (props: CropProps): JSX.Element => {
    const {
        source,
        cropShape = "circle",
        cropArea,
        backgroundColor = "#FFFFFF",
        opacity = 0.7,
        width,
        height,
        borderWidth = 2,
        maxZoom = 5,
        resizeMode = "contain",
        onCrop,
    } = props;

    cropArea.width = round(cropArea.width, 2);
    cropArea.height = round(cropArea.height, 2);

    assert(!isInRange(opacity, 1, 0), "opacity must be between 0 and 1");
    assert(maxZoom < 1, "maxZoom must be greater than 1");
    assert(width < cropArea.width, "width must be greater than crop area width");
    assert(height < cropArea.height, "height must be greater than crop area height");

    const _lastScale = useRef(1);
    let _lastTranslate = { x: 0, y: 0 };

    const trackScale = useMemo(() => new Animated.Value(0), []);
    const [scale] = useState(new Animated.Value(0));

    const [trackTranslationX] = useState(new Animated.Value(0));
    const [trackTranslationY] = useState(new Animated.Value(0));

    const [translateX] = useState(new Animated.Value(0));
    const [translateY] = useState(new Animated.Value(0));

    const [minZoom, setMinZoom] = useState(1);

    const imageSize = useRef({ width: NaN, height: NaN, rotation: 0 });

    const setImageSize = ({
        width,
        height,
        rotation,
    }: {
        width: number;
        height: number;
        rotation?: number;
    }) => {
        imageSize.current.width = width;
        imageSize.current.height = height;
        imageSize.current.rotation = rotation || 0;
    };

    const init = async () => {
        if (source.size) {
            setImageSize(source.size);
        } else {
            setImageSize(await computeImageSize(source.uri));
        }
        const _initialScale = computeContain(imageSize.current, cropArea);

        setMinZoom(_initialScale);
        scale.setValue(_initialScale);

        if (resizeMode === "cover") {
            scale.setValue(
                computeCover(getValue(scale), imageSize.current, { width, height }, cropArea),
            );
        }

        _lastScale.current = getValue(scale);

        // reset translation
        translateX.setValue(0);
        translateY.setValue(0);
        addScaleListener();
        addTranslationListeners();
        onCrop(cropImage);
    };

    useEffect(() => {
        void init();

        return () => {
            removeScaleListeners();
            removeTranslationListeners();
        };
    });

    // start: pinch gesture handler

    const onPinchGestureEvent = useCallback(
        (e: GestureEvent<{ scale: number }>) => {
            /*
        On some ansdroid zoom out is slower, but not all...
        const scaleFactor = e.nativeEvent.scale;
        if (Platform.OS === "android" && scaleFactor < 1) {
            const OLD_MAX = 1;
            const OLD_MIN = 0;

            const NEW_MAX = 1;
            const NEW_MIN = -1;

            // @ts-expect-error scale is read-only
            e.nativeEvent.scale =
                ((NEW_MAX - NEW_MIN) / (OLD_MAX - OLD_MIN)) * (scaleFactor - OLD_MAX) + NEW_MAX;
        }
        */

            Animated.event([{ nativeEvent: { scale: trackScale } }], {
                useNativeDriver: false,
            })(e);
        },
        [trackScale],
    );

    const addScaleListener = () => {
        trackScale.addListener(({ value }: { value: number }) => {
            // value always starts from 0
            scale.setValue(computeScale(value, _lastScale.current, maxZoom, minZoom));
        });
    };

    const removeScaleListeners = () => {
        trackScale.removeAllListeners();
    };

    const resetTranslate = useCallback(() => {
        // after scaling if crop area has blank space then
        // it will reset to fit image inside the crop area
        const scaleValue = getValue(scale);
        if (scaleValue < _lastScale.current) {
            const translateXValue = getValue(translateX);
            const translateYValue = getValue(translateY);
            const { max: maxTranslateX, min: minTranslateX } = translateRangeX(
                getValue(scale),
                imageSize.current,
                cropArea,
                minZoom,
            );

            if (!isInRange(translateXValue, maxTranslateX, minTranslateX)) {
                const toValue = translateXValue > 0 ? maxTranslateX : minTranslateX;
                Animated.timing(translateX, {
                    toValue,
                    duration: DEFAULT_ANIM_DURATION,
                    useNativeDriver: true,
                }).start(() => translateX.setValue(toValue));
            }

            const { max: maxTranslateY, min: minTranslateY } = translateRangeY(
                getValue(scale),
                imageSize.current,
                cropArea,
                minZoom,
            );

            if (!isInRange(translateYValue, maxTranslateY, minTranslateY)) {
                const toValue = translateYValue > 0 ? maxTranslateY : minTranslateY;
                Animated.timing(translateY, {
                    toValue,
                    duration: DEFAULT_ANIM_DURATION,
                    useNativeDriver: true,
                }).start(() => translateY.setValue(toValue));
            }
        }
    }, [cropArea, minZoom, scale, translateX, translateY]);

    const onPinchGestureStateChange = useCallback(
        ({ nativeEvent }: GestureEvent) => {
            if (nativeEvent.oldState === State.ACTIVE) {
                resetTranslate();
                // resetTranslate depends on _lastScale
                _lastScale.current = getValue(scale);
            }
        },
        [resetTranslate, scale],
    );

    // Event listener for mouse wheel zoom (only for web)
    const handleWheel = useCallback(
        (event: WheelEvent<HTMLDivElement>) => {
            if (Platform.OS === "web") {
                const zoomFactor = event.deltaY < 0 ? 1.1 : 0.9;
                onPinchGestureEvent({ nativeEvent: { scale: zoomFactor } } as GestureEvent<{
                    scale: number;
                }>);
                onPinchGestureStateChange({
                    nativeEvent: { oldState: State.ACTIVE },
                } as GestureEvent<{ oldState: State }>);
            }
        },
        [onPinchGestureEvent, onPinchGestureStateChange],
    );

    // end: pinch gesture handler

    // =================================================================

    // start: pan gesture handler

    const onPanGestureEvent = Animated.event(
        [
            {
                nativeEvent: {
                    translationX: trackTranslationX,
                    translationY: trackTranslationY,
                },
            },
        ],
        {
            useNativeDriver: false,
        },
    );

    const addTranslationListeners = () => {
        trackTranslationX.addListener(({ value }: { value: number }) => {
            const { max, min } = translateRangeX(
                getValue(scale),
                imageSize.current,
                cropArea,
                minZoom,
            );
            const last = _lastTranslate.x;
            translateX.setValue(computeTranslation(value, last, max, min));
        });

        trackTranslationY.addListener(({ value }: { value: number }) => {
            const { max, min } = translateRangeY(
                getValue(scale),
                imageSize.current,
                cropArea,
                minZoom,
            );
            const last = _lastTranslate.y;
            translateY.setValue(computeTranslation(value, last, max, min));
        });
    };

    const removeTranslationListeners = () => {
        translateX.removeAllListeners();
        translateY.removeAllListeners();
    };

    const onPanGestureStateChange = ({ nativeEvent }: GestureEvent) => {
        if (nativeEvent.oldState === State.ACTIVE) {
            _lastTranslate = { x: getValue(translateX), y: getValue(translateY) };
        }
    };

    // end: pan gesture handler

    const cropImage = async (
        quality: number = 1,
    ): Promise<{ uri: string; height: number; width: number }> => {
        assert(!isInRange(quality, 1, 0), "quality must be between 0 and 1");

        const scaleValue = getValue(scale);
        const translateXValue = getValue(translateX);
        const translateYValue = getValue(translateY);

        const scaledWidth = computeScaledWidth(scaleValue, imageSize.current, cropArea, minZoom);
        const scaledHeight = computeScaledHeight(scaleValue, imageSize.current, cropArea, minZoom);
        const scaledMultiplier = computeScaledMultiplier(imageSize.current, scaledWidth);

        const scaledSize = { width: scaledWidth, height: scaledHeight };
        const translate = computeTranslate(imageSize.current, translateXValue, translateYValue);

        const { max: maxTranslateX } = translateRangeX(
            getValue(scale),
            imageSize.current,
            cropArea,
            minZoom,
        );
        const { max: maxTranslateY } = translateRangeY(
            getValue(scale),
            imageSize.current,
            cropArea,
            minZoom,
        );

        const offset = computeOffset(
            scaledSize,
            imageSize.current,
            translate,
            maxTranslateX,
            maxTranslateY,
            scaledMultiplier,
        );
        const size = computeSize(cropArea, scaledMultiplier);
        const emitSize = computeSize(size, quality);
        const cropData = { offset, size, displaySize: emitSize };

        try {
            const croppedImageUri = await ImageEditor.cropImage(source.uri, cropData);
            let finalUri = croppedImageUri.uri;
            if (Platform.OS === "web") {
                finalUri = `data:${croppedImageUri.type};base64,${finalUri}`;
            }
            return { uri: finalUri, ...emitSize };
        } catch (e) {
            console.error("Failed to crop image!");
            throw e;
        }
    };

    const borderRadius = cropShape === "circle" ? Math.max(cropArea.height, cropArea.width) : 0;

    return (
        <PanGestureHandler
            minPointers={1}
            maxPointers={1}
            onGestureEvent={onPanGestureEvent}
            onHandlerStateChange={onPanGestureStateChange}
        >
            <PinchGestureHandler
                onGestureEvent={onPinchGestureEvent}
                onHandlerStateChange={onPinchGestureStateChange}
            >
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                <View onWheel={handleWheel} style={{ width, height, backgroundColor }}>
                    <MaskedView
                        style={styles.mask}
                        maskElement={
                            <View
                                style={[
                                    styles.overlay,
                                    {
                                        backgroundColor: `${backgroundColor}${getAlpha(opacity)}`,
                                    },
                                ]}
                            >
                                <View
                                    style={[
                                        styles.transparentMask,
                                        {
                                            ...cropArea,
                                            borderRadius,
                                        },
                                    ]}
                                />
                            </View>
                        }
                    >
                        <Animated.View
                            style={[
                                styles.center,
                                {
                                    transform: [{ translateX }, { translateY }],
                                },
                            ]}
                        >
                            <Animated.Image
                                source={source}
                                style={[
                                    styles.contain,
                                    {
                                        ...cropArea,
                                        transform: [{ scale }],
                                    },
                                ]}
                            />
                        </Animated.View>
                    </MaskedView>
                    <View
                        style={{
                            ...StyleSheet.absoluteFillObject,
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <View
                            style={{
                                ...cropArea,
                                borderWidth: borderWidth,
                                borderRadius,
                                borderColor: backgroundColor,
                            }}
                        />
                    </View>
                </View>
            </PinchGestureHandler>
        </PanGestureHandler>
    );
};

export default Crop;

const styles = StyleSheet.create({
    mask: { flex: 1 },
    center: { flex: 1, justifyContent: "center", alignItems: "center" },
    transparentMask: { backgroundColor: "#FFFFFF" },
    overlay: { flex: 1, justifyContent: "center", alignItems: "center" },
    contain: { resizeMode: "contain" },
});
